import { useRoute } from 'nuxt/app';
import { signOut } from '@/utils/auth';
import { errorCodes } from '~/constants';

export default defineNuxtPlugin(() => {
  const router = useRoute();
  const runtimeConfig = useConfig();
  const { $i18n } = useNuxtApp();
  const { t } = $i18n;

  const getSessionToken = () => {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('sessionToken') : null;
  };

  const hasTemporaryAuthorization = (headers: Headers) => {
    const authorization = headers.get('Authorization');
    const sessionToken = getSessionToken();
    return !!authorization && !!sessionToken && authorization !== sessionToken;
  };

  const $customFetch = $fetch.create({
    baseURL: `${runtimeConfig.value.public.apiUrl}/${router.params.partner}`,
    onRequest({ options }) {
      const headers = new Headers(options.headers as HeadersInit | undefined);
      const authorization = headers.get('Authorization');
      if (!authorization) {
        const token = getSessionToken();
        if (token) {
          headers.set('Authorization', token);
        }
      }
      const method = (options.method || 'GET').toString().toUpperCase();
      if (method === 'GET' && !headers.get('Cache-Control')) {
        headers.set('Cache-Control', 'no-store');
        headers.set('Pragma', 'no-cache');
      }
      options.headers = headers;
    },
    onResponseError: async ({ response, options }) => {
      const errorData = await response._data;
      const errorCode = errorData.Code;
      let errorMessage = errorData.Message || response.statusText;
      let useCustomMessage = false;
      const headers = new Headers(options.headers as HeadersInit | undefined);
      const isTemporaryAuthorization = hasTemporaryAuthorization(headers);

      switch (errorCode) {
        case errorCodes.ERR_WRONG_PASSWORD:
          errorMessage = t('common.wrongPassword') as string;
          useCustomMessage = true;
          break;
        case errorCodes.ERR_USER_BLOCKED:
          errorMessage = t('common.tooManyAttempts') as string;
          useCustomMessage = true;
          break;
        case errorCodes.ERR_ACTION_FORBIDDEN:
          errorMessage = t('common.forbiddenAction') as string;
          useCustomMessage = true;
          break;
        case errorCodes.ERR_IP_BLOCKED:
          if (!router.path.endsWith('sign-up')) {
            signOut();
            errorMessage = t('signIn.ip') as string;
            useCustomMessage = true;
          }
          break;
        case errorCodes.ERR_TOKEN_INVALID:
        case errorCodes.ERR_TOKEN_NOT_FOUND:
          if (!isTemporaryAuthorization) {
            signOut();
          }
          errorMessage = t('common.sessionExpired') as string;
          useCustomMessage = true;
          break;
        default:
          if ([401, 403].includes(response.status)) {
            if (!isTemporaryAuthorization) {
              signOut();
            }
          }
          break;
      }

      const error = new Error(errorMessage);

      if (!useCustomMessage) {
        Object.assign(error, errorData);
        if (!(error as any).response) {
          (error as any).response = {
            data: errorData
          };
        }
      } else {
        error.message = errorMessage;
        (error as any).response = null;
        (error as any).Message = errorMessage;
        (error as any).Code = errorCode;
      }

      return Promise.reject(error);
    }
  });

  return {
    provide: {
      customFetch: $customFetch
    }
  };
});
